export type Language = 'de' | 'en';

export interface Translations {
  common: {
    back: string;
    logout: string;
    save: string;
    cancel: string;
    loading: string;
    error: string;
    success: string;
  };
  landing: {
    title: string;
    subtitle: string;
    getStarted: string;
    artistLogin: string;
    features: {
      compete: string;
      compete_desc: string;
      music: string;
      music_desc: string;
      social: string;
      social_desc: string;
    };
  };
  login: {
    playerTitle: string;
    artistTitle: string;
    email: string;
    emailPlaceholder: string;
    password: string;
    passwordPlaceholder: string;
    confirmPassword: string;
    loginButton: string;
    registerButton: string;
    switchToRegister: string;
    switchToLogin: string;
    loginSubtitle: string;
    createAccount: string;
    registerSubtitle: string;
    forgotPassword: string;
    resetTitle: string;
    resetSubtitle: string;
    resetConfirmSubtitle: string;
    requestReset: string;
    resetEmailSent: string;
    resetToken: string;
    resetTokenPlaceholder: string;
    newPassword: string;
    resetPassword: string;
    resetSuccess: string;
    haveResetToken: string;
    passwordMin: string;
    passwordMismatch: string;
  };
  profile: {
    title: string;
    nickname: string;
    avatar: string;
    socials: string;
    visibility: string;
    visibilityDesc: string;
    language: string;
  };
  songs: {
    title: string;
    play: string;
    bpm: string;
    beats: string;
    noSongs: string;
  };
  game: {
    score: string;
    combo: string;
    perfect: string;
    good: string;
    miss: string;
    paused: string;
    resume: string;
    quit: string;
  };
  results: {
    title: string;
    yourScore: string;
    newBest: string;
    previousBest: string;
    accuracy: string;
    maxCombo: string;
    perfectHits: string;
    goodHits: string;
    missedHits: string;
    playAgain: string;
    viewLeaderboard: string;
    backToSongs: string;
  };
  leaderboard: {
    title: string;
    globalTitle: string;
    rank: string;
    player: string;
    score: string;
    filterAll: string;
    filterSong: string;
    filterArtist: string;
    daily: string;
    weekly: string;
    monthly: string;
    yearly: string;
    allTime: string;
    noScores: string;
    yourRank: string;
  };
  artist: {
    dashboard: string;
    mySongs: string;
    addSong: string;
    editSong: string;
    title: string;
    artistName: string;
    coverUrl: string;
    audioUrl: string;
    spotifyUrl: string;
    lanes: string;
    lanesDesc: string;
    segmentStart: string;
    segmentEnd: string;
    generateBeats: string;
    uploadAudio: string;
    uploadCover: string;
    status: string;
    pending: string;
    active: string;
    delete: string;
    confirmDelete: string;
  };
}

export const translations: Record<Language, Translations> = {
  de: {
    common: {
      back: 'Zurück',
      logout: 'Abmelden',
      save: 'Speichern',
      cancel: 'Abbrechen',
      loading: 'Lädt...',
      error: 'Fehler',
      success: 'Erfolg',
    },
    landing: {
      title: 'BeatApp',
      subtitle: 'Spüre den Rhythmus. Werde der Champion.',
      getStarted: 'Jetzt spielen',
      artistLogin: 'Künstler-Login',
      features: {
        compete: 'Wettkampf',
        compete_desc: 'Tritt auf der globalen Rangliste gegen andere Spieler an',
        music: 'Musik',
        music_desc: 'Spiele zu Songs von aufstrebenden Künstlern',
        social: 'Community',
        social_desc: 'Verbinde dich mit anderen Spielern und Künstlern',
      },
    },
    login: {
      playerTitle: 'Spieler-Login',
      artistTitle: 'Kuenstler-Login',
      email: 'E-Mail',
      emailPlaceholder: 'deine@email.de',
      password: 'Passwort',
      passwordPlaceholder: 'mindestens 8 Zeichen',
      confirmPassword: 'Passwort bestaetigen',
      loginButton: 'Anmelden',
      registerButton: 'Account erstellen',
      switchToRegister: 'Noch kein Account? Registrieren',
      switchToLogin: 'Schon einen Account? Anmelden',
      loginSubtitle: 'Melde dich an, um direkt loszulegen.',
      createAccount: 'Account erstellen',
      registerSubtitle: 'Erstelle deinen Account in wenigen Sekunden.',
      forgotPassword: 'Passwort vergessen?',
      resetTitle: 'Passwort zuruecksetzen',
      resetSubtitle: 'Wir senden dir einen Reset-Link per E-Mail.',
      resetConfirmSubtitle: 'Gib deinen Reset-Token und ein neues Passwort ein.',
      requestReset: 'Reset-Link senden',
      resetEmailSent: 'Reset-Link gesendet! Pruefe dein Postfach.',
      resetToken: 'Reset-Token',
      resetTokenPlaceholder: 'Token aus der E-Mail',
      newPassword: 'Neues Passwort',
      resetPassword: 'Passwort setzen',
      resetSuccess: 'Passwort aktualisiert. Bitte anmelden.',
      haveResetToken: 'Ich habe einen Reset-Token',
      passwordMin: 'Passwort muss mindestens 8 Zeichen haben.',
      passwordMismatch: 'Passwoerter stimmen nicht ueberein.',
    },

    profile: {
      title: 'Profil',
      nickname: 'Nickname',
      avatar: 'Profilbild',
      socials: 'Social Media',
      visibility: 'Profil sichtbar',
      visibilityDesc: 'Andere können auf dein Profil in der Rangliste klicken',
      language: 'Sprache',
    },
    songs: {
      title: 'Songs',
      play: 'Spielen',
      bpm: 'BPM',
      beats: 'Beats',
      noSongs: 'Noch keine Songs verfügbar',
    },
    game: {
      score: 'Punkte',
      combo: 'Kombo',
      perfect: 'Perfekt',
      good: 'Gut',
      miss: 'Verfehlt',
      paused: 'Pausiert',
      resume: 'Fortsetzen',
      quit: 'Beenden',
    },
    results: {
      title: 'Ergebnis',
      yourScore: 'Deine Punkte',
      newBest: 'Neuer Rekord!',
      previousBest: 'Bisheriger Rekord:',
      accuracy: 'Genauigkeit',
      maxCombo: 'Max Kombo',
      perfectHits: 'Perfekt',
      goodHits: 'Gut',
      missedHits: 'Verfehlt',
      playAgain: 'Nochmal spielen',
      viewLeaderboard: 'Rangliste',
      backToSongs: 'Zurück zu Songs',
    },
    leaderboard: {
      title: 'Rangliste',
      globalTitle: 'Globale Rangliste',
      rank: 'Rang',
      player: 'Spieler',
      score: 'Punkte',
      filterAll: 'Alle',
      filterSong: 'Nach Song',
      filterArtist: 'Nach Künstler',
      daily: 'Heute',
      weekly: 'Woche',
      monthly: 'Monat',
      yearly: 'Jahr',
      allTime: 'Alle Zeit',
      noScores: 'Noch keine Punkte',
      yourRank: 'Dein Rang',
    },
    artist: {
      dashboard: 'Künstler Dashboard',
      mySongs: 'Meine Songs',
      addSong: 'Song hinzufügen',
      editSong: 'Song bearbeiten',
      title: 'Titel',
      artistName: 'Künstlername',
      coverUrl: 'Cover URL',
      audioUrl: 'Audio URL',
      spotifyUrl: 'Spotify URL',
      lanes: 'Spuren',
      lanesDesc: 'Anzahl der Spuren im Spiel (1-4)',
      segmentStart: 'Start (ms)',
      segmentEnd: 'Ende (ms)',
      generateBeats: 'Beats generieren',
      uploadAudio: 'Audio hochladen',
      uploadCover: 'Cover hochladen',
      status: 'Status',
      pending: 'Ausstehend',
      active: 'Aktiv',
      delete: 'Löschen',
      confirmDelete: 'Song wirklich löschen?',
    },
  },
  en: {
    common: {
      back: 'Back',
      logout: 'Logout',
      save: 'Save',
      cancel: 'Cancel',
      loading: 'Loading...',
      error: 'Error',
      success: 'Success',
    },
    landing: {
      title: 'BeatApp',
      subtitle: 'Feel the rhythm. Become the champion.',
      getStarted: 'Start Playing',
      artistLogin: 'Artist Login',
      features: {
        compete: 'Compete',
        compete_desc: 'Challenge other players on the global leaderboard',
        music: 'Music',
        music_desc: 'Play along to songs from emerging artists',
        social: 'Community',
        social_desc: 'Connect with other players and artists',
      },
    },
    login: {
      playerTitle: 'Player Login',
      artistTitle: 'Artist Login',
      email: 'Email',
      emailPlaceholder: 'your@email.com',
      password: 'Password',
      passwordPlaceholder: 'at least 8 characters',
      confirmPassword: 'Confirm password',
      loginButton: 'Sign in',
      registerButton: 'Create account',
      switchToRegister: 'No account yet? Register',
      switchToLogin: 'Already have an account? Sign in',
      loginSubtitle: 'Sign in to get started right away.',
      createAccount: 'Create account',
      registerSubtitle: 'Create your account in seconds.',
      forgotPassword: 'Forgot password?',
      resetTitle: 'Reset password',
      resetSubtitle: 'We will email you a reset link.',
      resetConfirmSubtitle: 'Enter your reset token and a new password.',
      requestReset: 'Send reset link',
      resetEmailSent: 'Reset link sent! Check your inbox.',
      resetToken: 'Reset token',
      resetTokenPlaceholder: 'Token from the email',
      newPassword: 'New password',
      resetPassword: 'Set password',
      resetSuccess: 'Password updated. Please sign in.',
      haveResetToken: 'I have a reset token',
      passwordMin: 'Password must be at least 8 characters.',
      passwordMismatch: 'Passwords do not match.',
    },

    profile: {
      title: 'Profile',
      nickname: 'Nickname',
      avatar: 'Profile Picture',
      socials: 'Social Media',
      visibility: 'Profile visible',
      visibilityDesc: 'Others can click your profile in the leaderboard',
      language: 'Language',
    },
    songs: {
      title: 'Songs',
      play: 'Play',
      bpm: 'BPM',
      beats: 'Beats',
      noSongs: 'No songs available yet',
    },
    game: {
      score: 'Score',
      combo: 'Combo',
      perfect: 'Perfect',
      good: 'Good',
      miss: 'Miss',
      paused: 'Paused',
      resume: 'Resume',
      quit: 'Quit',
    },
    results: {
      title: 'Results',
      yourScore: 'Your Score',
      newBest: 'New Best!',
      previousBest: 'Previous best:',
      accuracy: 'Accuracy',
      maxCombo: 'Max Combo',
      perfectHits: 'Perfect',
      goodHits: 'Good',
      missedHits: 'Missed',
      playAgain: 'Play Again',
      viewLeaderboard: 'Leaderboard',
      backToSongs: 'Back to Songs',
    },
    leaderboard: {
      title: 'Leaderboard',
      globalTitle: 'Global Leaderboard',
      rank: 'Rank',
      player: 'Player',
      score: 'Score',
      filterAll: 'All',
      filterSong: 'By Song',
      filterArtist: 'By Artist',
      daily: 'Today',
      weekly: 'Week',
      monthly: 'Month',
      yearly: 'Year',
      allTime: 'All Time',
      noScores: 'No scores yet',
      yourRank: 'Your Rank',
    },
    artist: {
      dashboard: 'Artist Dashboard',
      mySongs: 'My Songs',
      addSong: 'Add Song',
      editSong: 'Edit Song',
      title: 'Title',
      artistName: 'Artist Name',
      coverUrl: 'Cover URL',
      audioUrl: 'Audio URL',
      spotifyUrl: 'Spotify URL',
      lanes: 'Lanes',
      lanesDesc: 'Number of lanes in the game (1-4)',
      segmentStart: 'Start (ms)',
      segmentEnd: 'End (ms)',
      generateBeats: 'Generate Beats',
      uploadAudio: 'Upload Audio',
      uploadCover: 'Upload Cover',
      status: 'Status',
      pending: 'Pending',
      active: 'Active',
      delete: 'Delete',
      confirmDelete: 'Really delete this song?',
    },
  },
};

export function getTranslations(lang: Language): Translations {
  return translations[lang];
}

export function detectLanguage(): Language {
  const stored = localStorage.getItem('preferredLanguage');
  if (stored === 'de' || stored === 'en') return stored;
  
  const browserLang = navigator.language.split('-')[0];
  return browserLang === 'de' ? 'de' : 'en';
}
